import { debounce } from "lodash-es";
import { once } from "~/jose_modern/once";
import { computePosition, autoUpdate, offset, size, flip } from "@floating-ui/dom";

// ===========================================================================
// Common code used in both non-searching and searching lot code autocomplete
// ===========================================================================

export class AutocompleteState {
  constructor() {
    this.selectedIndex = -1;
    this.suggestions = [];
  }

  reset() {
    this.selectedIndex = -1;
    this.suggestions = [];
  }
}

// Map floating dropdown DOM nodes to their reference input elements for ARIA cleanup
const dropdownToInput = new WeakMap();

export function createAutocompleteDropdown(element, suggestionsId) {
  const dropdown = document.createElement("div");
  dropdown.id = suggestionsId;
  dropdown.className = "dropdown-menu autocomplete-suggestions is-floating";
  dropdown.classList.add("is-hidden");
  dropdown.setAttribute("role", "listbox");
  dropdown.innerHTML = '<div class="dropdown-content"></div>';

  element.setAttribute("role", "combobox");
  element.setAttribute("aria-autocomplete", "list");
  element.setAttribute("aria-controls", suggestionsId);
  element.setAttribute("aria-expanded", "false");
  element.setAttribute("aria-haspopup", "listbox");

  // Render in a portal to avoid clipping/stacking issues
  dropdown.style.position = "fixed";
  dropdown.style.zIndex = "9999";
  dropdown.style.right = "auto";
  dropdown.style.left = "0"; // will be updated by floating-ui
  document.body.appendChild(dropdown);
  dropdownToInput.set(dropdown, element);

  return dropdown;
}

export function renderAutocompleteSuggestions(
  dropdown,
  suggestions,
  { showUpdating = false } = {}
) {
  const content = dropdown.querySelector(".dropdown-content");

  content.innerHTML = suggestions
    .map(
      (item, index) =>
        `<button type="button" class="dropdown-item" role="option" id="${dropdown.id}-item-${
          index
        }" data-index="${index}">${escapeHtml(item)}</button>`
    )
    .join("");

  if (showUpdating) {
    content.innerHTML += `<div class="dropdown-item autocomplete-status"><span class="icon is-small"><i class="fas fa-spinner-scale fa-pulse"></i></span><span>Searching...</span></div>`;
  }
}

export function updateAutocompleteSelection(
  dropdown,
  selectedIndex,
  inputElement,
  fillInput = false
) {
  const items = dropdown.querySelectorAll(".dropdown-item:not(.autocomplete-status)");
  const content = dropdown.querySelector(".dropdown-content");
  const input = inputElement;

  items.forEach((item) => {
    const itemIndex = parseInt(item.dataset.index, 10);
    if (itemIndex === selectedIndex) {
      item.classList.add("is-active");
      item.setAttribute("aria-selected", "true");

      if (input) {
        // Only fill the input when explicitly requested (keyboard navigation)
        if (fillInput) {
          const selectedText = item.textContent.trim();
          if (selectedText) {
            // Store original value before first navigation
            if (input._originalValueBeforeNavigation === undefined) {
              input._originalValueBeforeNavigation = input.value;
            }
            input.value = selectedText;
            // Don't fire input event during navigation - it's just a preview
          }
        }

        if (item.id) {
          input.setAttribute("aria-activedescendant", item.id);
        }
      }

      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.offsetHeight;
      const scrollTop = content.scrollTop;
      const scrollBottom = scrollTop + content.clientHeight;

      if (itemTop < scrollTop) {
        content.scrollTop = itemTop;
      } else if (itemBottom > scrollBottom) {
        content.scrollTop = itemBottom - content.clientHeight;
      }
    } else {
      item.classList.remove("is-active");
      item.setAttribute("aria-selected", "false");
    }
  });
}

export function showAutocompleteDropdown(element, dropdown) {
  // Just in case there are other visible autocomplete dropdowns, hide them.
  // Only relevant in edge-cases; the click-outside handling mostly takes care of it, but it can still happen.
  const allVisibleDropdowns = document.querySelectorAll(
    ".autocomplete-suggestions:not(.is-hidden)"
  );
  allVisibleDropdowns.forEach((visibleDropdown) => {
    if (visibleDropdown !== dropdown) {
      visibleDropdown.classList.add("is-hidden");
      const associatedInput = dropdownToInput.get(visibleDropdown);
      if (associatedInput) {
        associatedInput.setAttribute("aria-expanded", "false");
        associatedInput.removeAttribute("aria-activedescendant");
      }
    }
  });

  dropdown.classList.remove("is-hidden");
  element.setAttribute("aria-expanded", "true");
  if (typeof dropdown._updateFloating === "function") dropdown._updateFloating();
}

export function hideAutocompleteDropdown(element, dropdown) {
  dropdown.classList.add("is-hidden");
  element.setAttribute("aria-expanded", "false");
  element.removeAttribute("aria-activedescendant");
}

export function setupAutocompleteItemSelection(dropdown, { onSelect, onHover }) {
  dropdown.addEventListener("click", (e) => {
    const item = e.target.closest(".dropdown-item:not(.autocomplete-status)");
    if (item) {
      e.preventDefault();
      const index = parseInt(item.dataset.index, 10);
      if (!isNaN(index) && onSelect) {
        onSelect(index);
      }
    }
  });

  if (onHover) {
    dropdown.addEventListener("mouseover", (e) => {
      const item = e.target.closest(".dropdown-item:not(.autocomplete-status)");
      if (item && item.dataset.index) {
        const index = parseInt(item.dataset.index, 10);
        if (!isNaN(index)) {
          onHover(index);
          if (typeof dropdown._updateFloating === "function") dropdown._updateFloating();
        }
      }
    });
  }
}

export function setupAutocompleteKeyboardNavigation(
  element,
  dropdown,
  { state, onSelect, onCancel, onEnterWithNoSelection }
) {
  element.addEventListener("keydown", (e) => {
    const isVisible = !dropdown.classList.contains("is-hidden");

    if (!isVisible) return;

    const items = dropdown.querySelectorAll(".dropdown-item:not(.autocomplete-status)");
    if (items.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (state.selectedIndex < state.suggestions.length - 1) {
        state.selectedIndex++;
      } else {
        state.selectedIndex = 0;
      }
      // Mark that we're navigating to prevent re-filtering
      element._isNavigating = true;
      updateAutocompleteSelection(dropdown, state.selectedIndex, element, true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (state.selectedIndex > 0) {
        state.selectedIndex--;
      } else {
        state.selectedIndex = state.suggestions.length - 1;
      }
      // Mark that we're navigating to prevent re-filtering
      element._isNavigating = true;
      updateAutocompleteSelection(dropdown, state.selectedIndex, element, true);
    } else if (e.key === "Enter") {
      if (!dropdown.classList.contains("is-hidden")) {
        if (state.selectedIndex >= 0) {
          e.preventDefault();
          e.stopPropagation(); // Prevent event from bubbling to form submission handler
          if (onSelect) {
            onSelect(state.selectedIndex);
          }
        } else if (onEnterWithNoSelection) {
          e.preventDefault();
          onEnterWithNoSelection();
        }
        // If no handler, let Enter bubble up for form submission
      }
    } else if (e.key === "Escape") {
      if (onCancel) onCancel();
    }
  });
}

export function setupAutocompleteClickOutside(element, dropdown, onClickOutside) {
  const clickOutsideHandler = (e) => {
    if (!element.contains(e.target) && !dropdown.contains(e.target)) {
      onClickOutside();
    }
  };

  document.addEventListener("click", clickOutsideHandler);

  return () => {
    document.removeEventListener("click", clickOutsideHandler);
  };
}

export const escapeHtml = (text) => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

// Use Floating UI setup for dropdown positioning and sizing - had to introduce floating-ui
// because I couldn't get z-index right for all cases otherwise
export function setupFloatingDropdown(referenceElement, dropdown) {
  const updateFloatingPosition = () => {
    computePosition(referenceElement, dropdown, {
      placement: "bottom-end",
      strategy: "fixed",
      middleware: [
        offset(0),
        // Only allow horizontal alignment flip (end <-> start), never flip to top
        flip({ mainAxis: false, crossAxis: true, fallbackPlacements: ["bottom-start"] }),
        size({
          apply({ availableHeight, elements, rects }) {
            const refWidth = `${rects.reference.width}px`;
            Object.assign(elements.floating.style, {
              minWidth: refWidth,
              maxWidth: "300px",
              width: "max-content",
            });

            const isMobile = window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
            const heightLimit = isMobile ? 250 : 300;
            const contentEl = elements.floating.querySelector(".dropdown-content");
            if (contentEl) {
              const maxH = Math.max(0, Math.min(heightLimit, availableHeight - 4));
              Object.assign(contentEl.style, {
                maxHeight: `${maxH}px`,
                overflowY: "auto",
              });
              elements.floating.style.visibility = maxH <= 0 ? "hidden" : "visible";
            }
          },
        }),
      ],
    }).then(({ x, y }) => {
      Object.assign(dropdown.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  };

  const cleanupAutoUpdate = autoUpdate(referenceElement, dropdown, updateFloatingPosition);

  dropdown._updateFloating = updateFloatingPosition;
  updateFloatingPosition();

  return () => {
    cleanupAutoUpdate();
    delete dropdown._updateFloating;
  };
}

// =========================================================================
// initializeSingleLotCodeTypeahead: non-searching autocomplete of lot codes
// that are relevant for a specific line-item.
// =========================================================================

export function initializeSingleLotCodeTypeahead(input, dataSourceElement = null) {
  const sourceElement = dataSourceElement || input;

  if (!sourceElement.dataset.source) {
    sourceElement.dataset.source = "[]";
  }

  once(input, "lot-code-typeahead", () => {
    const state = new AutocompleteState();
    const suggestionsId = `${input.id || "typeahead"}-suggestions`;
    const dropdown = createAutocompleteDropdown(input, suggestionsId);
    const cleanupFloating = setupFloatingDropdown(input, dropdown);

    let lastSelectedValue = null;
    let suppressNextInputEvent = false;
    let suppressNextFocusOpen = false;
    let suggestions = [];
    try {
      const sourceData = sourceElement.dataset.source.trim();
      if (sourceData) {
        suggestions = JSON.parse(sourceData);
        if (!Array.isArray(suggestions)) {
          console.warn("Typeahead data source is not an array, using empty array");
          suggestions = [];
        }
      }
    } catch (e) {
      console.error(
        `Failed to parse typeahead data source: ${e} - source: ${sourceElement.dataset.source}`
      );
      suggestions = [];
    }

    const showSuggestions = () => {
      showAutocompleteDropdown(input, dropdown);
    };

    const hideSuggestions = () => {
      // Restore original value if user was navigating but didn't select
      if (input._originalValueBeforeNavigation !== undefined && state.selectedIndex >= 0) {
        input.value = input._originalValueBeforeNavigation;
        delete input._originalValueBeforeNavigation;
      }
      hideAutocompleteDropdown(input, dropdown);
      state.reset();
    };

    const selectItem = (index) => {
      if (index >= 0 && index < state.suggestions.length) {
        const selectedValue = state.suggestions[index];

        // Clear the navigation original value since we're committing to a selection
        delete input._originalValueBeforeNavigation;

        input.value = selectedValue;
        lastSelectedValue = selectedValue;

        // Ensure immediate re-focus does not reopen the menu
        suppressNextFocusOpen = true;
        hideSuggestions();

        // Prevent the programmatically dispatched input event from reopening the dropdown
        suppressNextInputEvent = true;
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.dispatchEvent(new Event("change", { bubbles: true }));
        input.dispatchEvent(
          new CustomEvent("lot-code-selected", {
            bubbles: true,
            detail: { value: selectedValue },
          })
        );
      }
    };

    const renderSuggestions = () => {
      renderAutocompleteSuggestions(dropdown, state.suggestions);
    };

    const filterAndShowSuggestions = (value) => {
      // For lot code fields, show all suggestions on empty input
      if (value.length === 0) {
        state.suggestions = suggestions;
      } else {
        state.suggestions = suggestions.filter((item) =>
          item.toLowerCase().includes(value.toLowerCase())
        );
      }

      state.selectedIndex = -1;

      if (state.suggestions.length === 0) {
        hideSuggestions();
        return;
      }

      // Don't show autocomplete if there's exactly 1 match and it was the last selected value
      if (
        state.suggestions.length === 1 &&
        state.suggestions[0] === lastSelectedValue &&
        value === lastSelectedValue
      ) {
        hideSuggestions();
        return;
      }

      renderSuggestions();
      showSuggestions();
    };

    setupAutocompleteItemSelection(dropdown, {
      onSelect: selectItem,
      onHover: (index) => {
        state.selectedIndex = index;
        updateAutocompleteSelection(dropdown, state.selectedIndex, null);
      },
    });

    setupAutocompleteKeyboardNavigation(input, dropdown, {
      state,
      onSelect: selectItem,
      onCancel: hideSuggestions,
    });

    const cleanupClickOutside = setupAutocompleteClickOutside(input, dropdown, hideSuggestions);

    const handleInput = debounce(function () {
      if (suppressNextInputEvent) {
        suppressNextInputEvent = false;
        return;
      }
      // Don't re-filter when navigating with arrows
      if (this._isNavigating) {
        this._isNavigating = false;
        return;
      }
      const value = this.value.toLowerCase().trim();
      filterAndShowSuggestions(value);
    }, 150);

    input.addEventListener("input", handleInput);

    // Handle focus to show suggestions immediately (even if the field is empty)
    input.addEventListener("focus", function () {
      if (suppressNextFocusOpen) {
        suppressNextFocusOpen = false;
        return;
      }
      const value = this.value.toLowerCase().trim();
      filterAndShowSuggestions(value);
    });

    const floatingToggleHandler = () => {
      hideSuggestions();
    };
    document.addEventListener("floating-toggle-clicked", floatingToggleHandler);

    const form = input.closest("form");
    let cleanupFormSubmit = null;

    if (form) {
      const submitHandler = () => {
        input.value = input.value.trim();
      };

      form.addEventListener("submit", submitHandler);
      cleanupFormSubmit = () => form.removeEventListener("submit", submitHandler);
    }

    input._cleanup = () => {
      hideSuggestions();
      cleanupClickOutside();
      handleInput.cancel();
      document.removeEventListener("floating-toggle-clicked", floatingToggleHandler);
      cleanupFloating();
      if (dropdown.parentNode) dropdown.parentNode.removeChild(dropdown);
      if (cleanupFormSubmit) cleanupFormSubmit();
    };
  });
}

function initializeTypeaheadInContainer(container = document) {
  const lotCodeWrappers = container.querySelectorAll(
    '.lot-codes-wrapper[data-provide="typeahead"]'
  );

  // Handle the cases where multiple lot codes are allowed
  lotCodeWrappers.forEach((wrapper) => {
    const inputs = wrapper.querySelectorAll("input.lot-code");
    inputs.forEach((input) => {
      initializeSingleLotCodeTypeahead(input, wrapper);
    });
  });

  // Handle the standalone lot code inputs
  const standaloneInputs = container.querySelectorAll('input[data-provide="typeahead"]');
  standaloneInputs.forEach((input) => {
    // Only initialize if not already inside a lot-codes-wrapper
    if (!input.closest('.lot-codes-wrapper[data-provide="typeahead"]')) {
      initializeSingleLotCodeTypeahead(input);
    }
  });
}

export function initializeLotCodeTypeahead() {
  // Defer to next tick to ensure any just-inserted DOM is present
  setTimeout(() => {
    initializeTypeaheadInContainer(document);
  });

  once(document, "lot-code-typeahead-partial-handler", () => {
    document.addEventListener("partial:replace", (event) => {
      if (event.element) {
        const allInputs = event.element.querySelectorAll(
          'input.lot-code, input[data-provide="typeahead"]'
        );
        allInputs.forEach((input) => {
          if (input._cleanup) {
            input._cleanup();
            delete input._cleanup;
          }
        });
      }

      setTimeout(() => {
        if (event.container) {
          initializeTypeaheadInContainer(event.container);
        }
      });
    });
  });
}
